import type { App } from "vue";
import { i18n } from "./i18n";

/**
 * The last line of defence between a throw anywhere in the render tree and a blank tab.
 *
 * **Why this exists at all.** `src/shared/money.ts` throws `NonIntegerAmountError` on a
 * non-integer amount BY DESIGN — a float reaching that module means something upstream already
 * did float arithmetic on money, and rounding it away would hide the bug at exactly the point it
 * becomes a number on screen. `AuthorizeDialog.vue`'s own `setCount` comment records what that
 * throw does with no handler installed: it "took down the render effect and froze the dialog on a
 * stale total." Verified directly for THIS module too (mount `features/day/routes/index.vue` with
 * one kiosk carrying a fractional `exceptionAmount` and no handler): Vue swallows the exception
 * internally, logs two "Unhandled error during execution of…" warnings, and — because nothing
 * calls `instance.appContext.config.errorHandler` (there isn't one) — the render effect never
 * recovers. The component's own subtree collapses to an empty comment node and never updates
 * again: `wrapper.html()` reads `""`. A manager mid cash-count sees nothing, ever, with nothing in
 * the UI saying why.
 *
 * **Two failure surfaces, because Vue's error routing has two distinct gaps:**
 *  1. `app.config.errorHandler` — set below. This is what Vue calls once no `errorCaptured` hook
 *     anywhere in the ancestor chain claims the error (see Vue's own `handleError`); without it,
 *     Vue's fallback in dev is to log a warning and otherwise leave the failed component's effect
 *     wedged exactly as the paragraph above describes. Setting *any* function here is what turns a
 *     wedged effect back into a normal, recoverable one — the effect completes instead of dying
 *     mid-throw.
 *  2. Recovering the effect is not the same as showing something. Once `app.config.errorHandler`
 *     absorbs the error, the component whose render function threw still produces an EMPTY subtree
 *     for that pass — there was no vnode tree to patch in, because the throw happened while one was
 *     still being built. So the fix also needs a boundary that actually puts legible content on the
 *     screen. That boundary is deliberately NOT a Vue component wrapping `<App>` — a Vue-rendered
 *     fallback still depends on Vue's own render pipeline being healthy, which is exactly what just
 *     failed. It is a plain DOM node appended straight to `document.body`, written to with no
 *     reactivity and no render pass involved, so it appears even if the rest of the page is a
 *     comment node.
 *
 * Both paths funnel through `reportError`, so there is exactly one thing an operator sees and
 * exactly one console line per failure — never a banner with a silent console, and never a logged
 * error with nothing on screen. "A blank screen and a silent console are the same thing to an
 * operator" — the whole reason this file exists.
 */

let banner: HTMLElement | null = null;

/** Creates the banner element once and reuses it — a second failure updates the same message
 *  rather than stacking a new element behind (or on top of) the first. */
function renderErrorBanner(): HTMLElement {
  if (banner) return banner;
  banner = document.createElement("div");
  banner.setAttribute("role", "alert");
  banner.setAttribute("data-testid", "app-render-error");
  banner.className = "app-render-error";
  document.body.appendChild(banner);
  return banner;
}

/**
 * Logs first, unconditionally, before touching the DOM — so a banner that itself somehow fails to
 * append still leaves a console line, never the reverse.
 */
function reportError(err: unknown, info: string): void {
  console.error(`[backoffice] unhandled error (${info})`, err);
  renderErrorBanner().textContent = i18n.global.t("app.renderError");
}

/**
 * Call exactly once, on the real app instance, before `app.mount(...)` — mirrors
 * `app/theme-mode.ts`'s `initThemeMode()` and `app/session-guard.ts`'s
 * `watchForExpiredSession()`: one wiring call from `main.ts`, no further configuration.
 */
export function installErrorHandler(app: App): void {
  app.config.errorHandler = (err, _instance, info) => {
    reportError(err, info);
  };
}
